/**
 * Feed Service
 *
 * Service for fetching feed posts from the Feed DynamoDB table.
 * Posts are created by the feed digest pipeline when reports finalize.
 */

import ApiService from './ApiService';
import type { FeedTask } from '@customTypes/objectiveFeed';

export type FeedInteractionType = 'title_click' | 'action_click';

export interface FeedInteractionEntry {
  user_id: string;
  timestamp: string;
}

export interface FeedAction {
  label: string;
  message: string;
}

export type StatusItemStatus = 'completed' | 'in_progress' | 'needs_attention' | 'in_the_works';

export interface StatusItem {
  text: string;
  status: StatusItemStatus;
  task_key?: string;
}

export type TodoCheckType = 'connect_platform' | 'enable_automation';

export interface UpdateTodo {
  label: string;
  check: TodoCheckType;
  platform?: string;
}

export interface FeedPost {
  post_id: string;
  entity_type: string;
  title: string;
  body: string;
  source: { type: string; report_id: string; s3_path: string };
  severity: 'critical' | 'warning' | 'info';
  status: string;
  action: FeedAction;
  metadata: Record<string, any> & {
    /** Multiple artifact actions for feed posts */
    actions?: FeedAction[];
    /** Structured status items */
    status_items?: StatusItem[];
    /** Optional follow-up question for two-part updates */
    question?: string;
    /** Self-contained prompt for background task execution when user clicks "Do it" */
    action_prompt?: string;
    /** User blockers that must be resolved before Leo can act */
    todos?: UpdateTodo[];
  };
  tasks?: FeedTask[];
  is_read?: boolean;
  title_clicked_by?: FeedInteractionEntry[];
  action_clicked_by?: FeedInteractionEntry[];
  objective_id?: string;
  created_by_user_name?: string;
  created_at: string;
  updated_at: string;
}

export interface FeedResponse {
  items: FeedPost[];
  next_cursor: string | null;
}

class FeedService {
  /**
   * List feed posts for the current user's company.
   *
   * @param limit - Max items to return (default 20)
   * @param cursor - Pagination cursor from previous response
   * @returns Paginated feed response
   */
  public async getFeed(limit?: number, cursor?: string): Promise<FeedResponse> {
    const params: Record<string, any> = {};
    if (limit) params.limit = limit;
    if (cursor) params.cursor = cursor;

    const { success, data, error } = await ApiService.get<FeedResponse>(
      '/feed',
      params,
    );

    if (success && data) {
      return data;
    }

    throw new Error(error || 'Failed to fetch feed');
  }

  /**
   * Fetch a specific feed post by ID.
   *
   * @param postId - The post ID
   * @returns The feed post
   */
  public async getPost(postId: string): Promise<FeedPost> {
    const { success, data, error } = await ApiService.get<FeedPost>(
      `/feed/${postId}`,
    );

    if (success && data) {
      return data;
    }

    throw new Error(error || 'Failed to fetch feed post');
  }

  /**
   * Record a click interaction on a feed post.
   * Fire-and-forget — errors are logged but not thrown.
   *
   * @param postId - The post ID
   * @param interactionType - 'title_click' or 'action_click'
   */
  public async trackInteraction(
    postId: string,
    interactionType: FeedInteractionType,
  ): Promise<void> {
    try {
      await ApiService.post(`/feed/${postId}/interaction`, {
        interaction_type: interactionType,
      });
    } catch (err) {
      console.error('Failed to track feed interaction:', err);
    }
  }

  /**
   * Dismiss a feed post for the current user.
   *
   * @param postId - The post ID to dismiss
   */
  public async dismissPost(postId: string): Promise<void> {
    const { success, error } = await ApiService.post(`/feed/${postId}/dismiss`, {});
    if (!success) {
      throw new Error(error || 'Failed to dismiss feed post');
    }
  }

  /**
   * Restore an archived feed post so it appears in the feed again.
   *
   * @param postId - The post ID to restore
   */
  public async restorePost(postId: string): Promise<void> {
    const { success, error } = await ApiService.post(`/feed/${postId}/restore`, {});
    if (!success) {
      throw new Error(error || 'Failed to restore feed post');
    }
  }

  /**
   * Dismiss multiple feed posts at once for the current user.
   *
   * @param postIds - Array of post IDs to dismiss
   * @returns The number of posts successfully dismissed
   */
  public async dismissAllPosts(postIds: string[]): Promise<number> {
    const { success, data, error } = await ApiService.post<
      { post_ids: string[] },
      { dismissed_count: number }
    >('/feed/dismiss-all', { post_ids: postIds });

    if (success && data) {
      return data.dismissed_count;
    }

    throw new Error(error || 'Failed to dismiss feed posts');
  }
}

export default new FeedService();
